import {
  HttpErrorResponse,
  HttpHandler,
  HttpHandlerFn,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import type { ExchangeRefreshToken } from '@flexyoo/api/models';
import { AuthenticationService } from '@flexyoo/auth';
import { FlexyooAlertsService } from '@flexyoo/common';
import { LoggerService } from '@flexyoo/core';
import { blobToText } from '@flexyoo/shared/functions';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, filter, finalize, first, map, switchMap, take, tap } from 'rxjs/operators';

// Error codes for which a dedicated UI (e.g. a dialog with the interpolated message) is
// already shown by the component that made the request - the generic alert would only
// duplicate it and, since it has no access to the interpolation params, show it unresolved.
const ERROR_CODES_WITH_DEDICATED_HANDLING = [
  'EMPLOYEE_HAS_CASH_BENEFIT_IN_SELECTED_STATE_IN_BASKET',
];

// Upload rejections. 413 is usually produced by the reverse proxy, so its body is HTML
// rather than the { Errors: [...] } shape displayErrorPopup knows how to read - those get
// a translated message of their own instead of being swallowed by the default branch.
const UPLOAD_REJECTED_STATUSES: Readonly<Record<number, string>> = {
  413: 'errors.file-too-large-backend',
  415: 'errors.invalid-file-type-backend',
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function isSuppressedErrorCode(entry: unknown): boolean {
  const code = asRecord(entry)?.['ErrorCode'];
  return typeof code === 'string' && ERROR_CODES_WITH_DEDICATED_HANDLING.includes(code);
}

@Injectable()
export class GlobaltInterceptorService implements HttpInterceptor {
  isRefreshingToken = false;
  tokenSubject: BehaviorSubject<string | false | null> = new BehaviorSubject<string | false | null>(
    null
  );
  private readonly logger = inject(LoggerService);
  private readonly flexyooAlertsService = inject(FlexyooAlertsService);

  constructor(
    private auth: AuthenticationService,
    private translate: TranslateService
  ) {}

  // add jwt - access token to each request
  addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    if (token && token !== '') {
      return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
    }
    return req;
  }

  logoutUser(failedUrl?: string) {
    // Route to the login page (implementation up to you)
    this.auth.logout(failedUrl);
    return throwError('');
  }

  private handle400Error(method: string, error: HttpErrorResponse) {
    this.logger.error('**** ERROR INTERCEPT ****');
    this.logger.error(error.name);
    this.logger.error(error.error);
    this.logger.error(error.message);
    this.logger.error('**************************');

    if (
      error &&
      error.status === 400 &&
      error.error &&
      error.error === 'No client principal found'
    ) {
      // If we get a 400 and the error message is 'invalid_grant', the token is no longer valid so logout.
      return this.logoutUser();
    }

    return this.displayErrorPopup(method, error);
  }

  private handle401Error(req: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshingToken) {
      this.isRefreshingToken = true;

      // Reset here so that the following requests wait until the token
      // comes back from the refreshToken call.
      this.tokenSubject.next(null);
      const token = {
        AccessToken: this.auth.getAccessToken(),
        RefreshToken: this.auth.getRefreshToken(),
      };

      return this.auth.refreshToken(token).pipe(
        switchMap((newToken: ExchangeRefreshToken) => {
          if (newToken) {
            this.tokenSubject.next(newToken.AccessToken);
            return next.handle(this.addToken(req, newToken.AccessToken));
          }
          // If we don't get a new token, we are in trouble so logout.
          return this.logoutUser();
        }),
        catchError(() => {
          // Signal failure to requests waiting in the else-branch below so they
          // don't hang forever waiting for a token that will never arrive.
          this.tokenSubject.next(false);
          return this.logoutUser();
        }),
        finalize(() => {
          this.isRefreshingToken = false;
        })
      );
    } else {
      return this.tokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((token) => {
          // `false` is the "refresh failed" marker, not a secret being compared
          // nosec
          if (token === false) {
            return throwError(() => new Error('Token refresh failed'));
          }
          return next.handle(this.addToken(req, token));
        })
      );
    }
  }

  private handleUploadRejected(error: HttpErrorResponse) {
    this.flexyooAlertsService.addAlert({
      text: this.translate.instant(UPLOAD_REJECTED_STATUSES[error.status]),
      type: 'warn',
    });

    return throwError(error);
  }

  private handleSaveComplete() {
    this.flexyooAlertsService.addAlert({
      text: this.translate.instant('general.save-success'),
      type: 'success',
    });
  }

  private isFailedAcceptedResult(body: unknown): boolean {
    return (
      !!body &&
      typeof body === 'object' &&
      'IsSuccess' in body &&
      (body as { IsSuccess: unknown }).IsSuccess === false
    );
  }

  private getTranslation(key: string) {
    const result = this.translate.instant(key);
    return result === key ? null : result;
  }

  private getErrorTranslation(method: string, code: string) {
    return (
      this.getTranslation(`error_codes.[${method}].${code}`) ||
      this.getTranslation(`error_codes.${code}`)
    );
  }

  private displayErrorPopup(method: string, error: any) {
    // TODO: Use ExceptionMiddleware format from backend ?
    const errorAlertType = new RegExp(/^4[\d]{2}$/).test(error?.status) ? 'warn' : 'error';
    if (error.error) {
      const err =
        error.error instanceof Blob
          ? blobToText(error.error).pipe(map((data) => JSON.parse(data)))
          : of(error.error);

      err
        .pipe(
          first(),
          // A body we cannot parse (HTML from a proxy, an unexpected shape) must still end
          // with a visible alert instead of an uncaught error that silently drops the popup.
          catchError(() => of(null))
        )
        .subscribe((errText) => {
          let messages: string[] | null;
          try {
            messages = this.extractErrorMessages(method, errText);
          } catch {
            messages = null;
          }

          // `[]` means the body was understood and deliberately produced no alert (a code with
          // dedicated UI handling); only an unrecognised body falls back to the generic message.
          if (messages === null) {
            messages = [this.getTranslation('errors.unexpected') || 'Something went wrong'];
          }

          messages.forEach((text) =>
            this.flexyooAlertsService.addAlert({ text, type: errorAlertType })
          );
        });
    } else {
      this.flexyooAlertsService.addAlert({
        text: this.getTranslation('errors.unexpected') || 'Something went wrong',
        type: errorAlertType,
      });
    }

    return throwError(error);
  }

  /**
   * Turns any error body the backend may produce into the list of messages to show.
   *
   * Three shapes are in play: the app's own `[{ ErrorCode, Message }]` array, FluentValidation's
   * `{ Errors: [{ PropertyName, ErrorMessage }] }`, and ASP.NET's ValidationProblemDetails, whose
   * `errors` is a *dictionary* of field -> messages. Treating the last one as an array used to
   * throw inside the subscriber, which swallowed the popup entirely (bug 49218).
   *
   * Returns `null` when the body is not one of those shapes, so the caller can fall back to a
   * generic message; an empty array means the body was understood but every message in it is
   * suppressed on purpose (a code whose component shows its own dialog).
   */
  private extractErrorMessages(method: string, body: unknown): string[] | null {
    if (typeof body === 'string') {
      // Proxies answer with an HTML page rather than the JSON contract; showing its markup
      // would be worse than the generic fallback.
      return !body.trim() || body.trim().startsWith('<') ? null : [body];
    }

    if (Array.isArray(body)) {
      const messages = body.flatMap((e) => this.messageFromErrorCode(method, e));

      // Nothing to say and nothing deliberately hidden means the entries carried no message
      // we understand - better the generic alert than silence.
      return messages.length || body.some(isSuppressedErrorCode) ? messages : null;
    }

    const record = asRecord(body);
    return record ? this.messagesFromErrorObject(method, record) : null;
  }

  /** The object half of {@link extractErrorMessages}; same `null` / empty-array contract. */
  private messagesFromErrorObject(
    method: string,
    record: Record<string, unknown>
  ): string[] | null {
    const errors = record['Errors'] ?? record['errors'];

    if (Array.isArray(errors)) {
      const messages = errors.flatMap((e) => this.messageFromValidationError(e));
      return messages.length ? messages : null;
    }

    const errorsRecord = asRecord(errors);
    if (errorsRecord) {
      // ValidationProblemDetails: { "field": ["message", ...], ... }
      const messages = Object.values(errorsRecord).flatMap((value) =>
        (Array.isArray(value) ? value : [value]).filter(
          (message): message is string => typeof message === 'string' && !!message
        )
      );

      return messages.length ? messages : null;
    }

    if (typeof errors === 'string' && errors) {
      return [errors];
    }

    // A single error object returned outside of an array...
    const messages = this.messageFromErrorCode(method, record);
    if (messages.length || isSuppressedErrorCode(record)) {
      return messages;
    }

    // ...or a ProblemDetails with no `errors` dictionary.
    const title = record['title'] ?? record['Title'];
    return typeof title === 'string' && title ? [title] : null;
  }

  private messageFromErrorCode(method: string, entry: unknown): string[] {
    const e = asRecord(entry);
    if (!e) {
      return typeof entry === 'string' && entry ? [entry] : [];
    }

    const errorCode = e['ErrorCode'];
    if (typeof errorCode === 'string' && ERROR_CODES_WITH_DEDICATED_HANDLING.includes(errorCode)) {
      return [];
    }

    const text =
      (typeof errorCode === 'string' ? this.getErrorTranslation(method, errorCode) : null) ||
      e['Message'] ||
      e['message'];

    return typeof text === 'string' && text ? [text] : [];
  }

  private messageFromValidationError(entry: unknown): string[] {
    if (typeof entry === 'string') {
      return entry ? [entry] : [];
    }

    const e = asRecord(entry);
    if (!e) {
      return [];
    }

    const errMsg = e['ErrorMessage'] ?? e['errorMessage'] ?? e['Message'] ?? e['message'];
    if (typeof errMsg !== 'string' || !errMsg) {
      return [];
    }

    if (errMsg.startsWith('backend.')) {
      return [this.getTranslation(errMsg) || errMsg];
    }

    const propertyName = e['PropertyName'] ?? e['propertyName'];
    return [propertyName ? `${propertyName}: ${errMsg}` : errMsg];
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<any> {
    if (req.url.includes('i18n')) {
      return next.handle(req);
    }
    return next.handle(this.addToken(req, this.auth.getAccessToken())).pipe(
      tap((evt) => {
        if (req.headers.get('skipSuccessMessage')) {
          return;
        }
        if (evt instanceof HttpResponse) {
          switch (evt.status) {
            case 201:
            case 202:
              if (!this.isFailedAcceptedResult(evt.body)) {
                this.handleSaveComplete();
              }
              break;
            default:
              break;
          }
        }
      }),
      catchError((error) => {
        if (error instanceof HttpErrorResponse) {
          switch (error.status) {
            case 401:
              return this.handle401Error(req, next);
            case 400:
              if (req.headers.get('skipErrorMessage')) {
                return throwError(error);
              }
              return this.handle400Error(req.method, error);
            case 500:
              return this.displayErrorPopup(req.method, error);
            default:
              if (UPLOAD_REJECTED_STATUSES[error.status]) {
                if (req.headers.get('skipErrorMessage')) {
                  return throwError(error);
                }
                return this.handleUploadRejected(error);
              }
              return throwError(error);
          }
        } else {
          return throwError(error);
        }
      })
    );
  }
}

export function authInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn) {
  // Inject the current `AuthService` and use it to get an authentication token:
  const injector = inject(Injector);
  const authService = injector.get(AuthenticationService);
  if (authService.getAccessToken) {
    const token = authService.getAccessToken();

    const newReq =
      token && token !== '' ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : req;

    return next(newReq);
  } else {
    return next(req);
  }
}
